import { DragControls, DragControlsProps } from '../DragControls';
import { makeQueryFilter, type NavMesh, NavMeshQuery } from '@slope/navmesh/core';
import { useNavMeshRaycast, type UseNavMeshRaycastOptions } from '@slope/navmesh/r3f';
import { NavMeshHelper } from '@slope/navmesh/three';
import { toVec3 } from '@slope/three';
import { useEffect, useMemo, useRef } from 'react';
import { Box3 } from 'three';
import { SpringValue } from '@react-spring/three';
import * as THREE from 'three';
import { useMutableCallback } from '@slope/hooks';

const vec3 = /*@__PURE__ */ new THREE.Vector3();

export type NavMeshDragControlsProps = DragControlsProps &
  UseNavMeshRaycastOptions & {
    /**
     * The nav mesh to use for raycasting and position snapping.
     */
    navMesh: NavMesh;
  };

/**
 * Drag controls that snap to a nav mesh.
 * This component uses the `NavMeshQuery` to find the closest point on the nav mesh to the current pointer position.
 */
export const NavMeshDragControls: React.FC<NavMeshDragControlsProps> = ({
  ref: _,
  navMesh,
  children,
  position,
  halfExtents,
  flags,
  ...restProps
}) => {
  const springRef = useRef<SpringValue<THREE.Vector3Tuple>>(null!);
  const project = useNavMeshRaycast(navMesh, { halfExtents, flags });

  const query = useMemo(() => new NavMeshQuery(navMesh), [navMesh]);

  const updatePosition = useMutableCallback((matrix: THREE.Matrix4) => {
    const pos = vec3.setFromMatrixPosition(matrix);

    const match = query.findClosestPoint(pos, {
      halfExtents,
      filter: flags ? makeQueryFilter(flags) : undefined,
    });

    const matchedPos = match.success ? toVec3(match.point, vec3) : undefined;

    if (matchedPos) {
      springRef.current.start(matchedPos.toArray());
    }
  });

  useEffect(() => {
    const point = position
      ? toVec3(position, vec3)
      : new Box3().setFromObject(new NavMeshHelper(navMesh)).getCenter(vec3);

    updatePosition(new THREE.Matrix4().setPosition(point));
  }, [navMesh, position, halfExtents, flags, query, updatePosition]);

  return (
    <DragControls
      project={project}
      {...restProps}
      springRef={springRef}
      spring={false}
      onDrag={updatePosition}
    >
      {children}
    </DragControls>
  );
};
